import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchDoctorAppointments,
  fetchAppointmentDetails,
  fetchAppointmentSummary,
} from "../../services/appointmentsService";

// Updated Appointment type to match backend
interface AttachedDocument {
  appointment: number;
  document: {
    document_id: string;
    patient: number;
    patient_name: string;
    name: string;
    document_type: string;
    filename: string;
    file_size_mb: number;
    created_at: string;
  };
  attached_at: string;
  attached_by: string;
  notes: string;
}

interface Appointment {
  appointment_id: number;
  patient: number;
  patient_name: string;
  doctor: number;
  doctor_name: string;
  doctor_specialization: string;
  appointment_date: string;
  reason: string;
  status: string;
  status_display: string;
  notes: string;
  attached_documents: AttachedDocument[];
  document_requests: any[];
  created_at: string;
  updated_at: string;
}

const AppointmentsPage: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  // Toast auto-hide effect
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDoctorAppointments()
      .then((data) => {
        setAppointments(Array.isArray(data) ? data : data.results || []);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error || "Failed to load appointments.";
        setError(msg);
        setToastMessage(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSelectAppointment = async (app: Appointment) => {
    setLoading(true);
    setShowPatientDetails(false);
    setSummary(null);
    setError(null);
    try {
      const details = await fetchAppointmentDetails(
        app.appointment_id.toString()
      );
      setSelected(details);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || "Failed to load appointment details.";
      setError(msg);
      setToastMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleShowSummary = async (appointmentId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAppointmentSummary(appointmentId.toString());
      setSummary(
        data.summary
          ? `${data.summary}\n\nGenerated at: ${new Date(
              data.generated_at
            ).toLocaleString()}\nDocument count: ${data.document_count}`
          : "No summary available."
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to load summary.";
      setError(msg);
      setToastMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = (id: number) => {
    setAppointments((prev) =>
      prev.map((app) =>
        app.appointment_id === id ? { ...app, status: "accepted" } : app
      )
    );
    if (selected && selected.appointment_id === id)
      setSelected({ ...selected, status: "accepted" });
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-red-600 text-white px-6 py-3 rounded shadow-lg transition-all animate-slide-in">
          {toastMessage}
        </div>
      )}
      <div className="flex flex-1">
        <div className="flex-1 bg-gray-50 p-0">
          <div className="flex flex-col md:flex-row gap-8 w-full h-full p-8">
            <div className="w-full md:w-1/2 bg-white rounded-xl shadow p-6">
              <h2 className="text-xl font-bold text-blue-700 mb-4">
                Pending Appointments
              </h2>
              {loading && <div className="text-blue-600 mb-2">Loading...</div>}
              {error && <div className="text-red-600 mb-2">{error}</div>}
              <ul className="divide-y">
                {appointments.filter((a) => a.status === "pending").length ===
                  0 &&
                  !loading && (
                    <li className="text-gray-400 py-8 text-center">
                      No pending appointments
                    </li>
                  )}
                {appointments
                  .filter((a) => a.status === "pending")
                  .map((app) => (
                    <li
                      key={app.appointment_id}
                      className={`py-4 px-2 cursor-pointer rounded-lg hover:bg-blue-50 ${
                        selected?.appointment_id === app.appointment_id
                          ? "bg-blue-100"
                          : ""
                      }`}
                      onClick={() => handleSelectAppointment(app)}
                    >
                      <div className="font-semibold text-gray-800">
                        {app.patient_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(app.appointment_date).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {app.reason}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2 bg-white rounded-xl shadow p-6 flex flex-col">
              <h2 className="text-xl font-bold text-blue-700 mb-4">
                Appointment Details
              </h2>
              {!selected ? (
                <div className="text-gray-400 text-center my-auto">
                  Select an appointment to view details
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="font-semibold text-lg text-gray-800">
                      {selected.patient_name}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      {new Date(selected.appointment_date).toLocaleString()}
                    </div>
                    <div className="text-gray-700 mb-2">
                      Doctor: {selected.doctor_name} (Specialization:{" "}
                      {selected.doctor_specialization})
                    </div>
                    <div className="text-gray-700 mb-2">
                      Reason: {selected.reason}
                    </div>
                    <div className="text-gray-700 mb-2">
                      Status: {selected.status_display}
                    </div>
                    <div className="text-gray-700 mb-2">
                      Notes: {selected.notes}
                    </div>
                  </div>
                  <div className="mb-4">
                    <h3 className="font-semibold text-md text-blue-700 mb-2">
                      Attached Documents
                    </h3>
                    {selected.attached_documents.length === 0 ? (
                      <div className="text-gray-400">
                        No documents attached.
                      </div>
                    ) : (
                      <ul className="list-disc pl-5">
                        {selected.attached_documents.map((doc, idx) => (
                          <li key={doc.document.document_id} className="mb-2">
                            <div className="font-semibold">
                              {doc.document.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Type: {doc.document.document_type}
                            </div>
                            <div className="text-xs text-gray-500">
                              Filename: {doc.document.filename} (
                              {doc.document.file_size_mb} MB)
                            </div>
                            <div className="text-xs text-gray-500">
                              Attached by: {doc.attached_by} at{" "}
                              {new Date(doc.attached_at).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              Notes: {doc.notes}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mb-4">
                    <h3 className="font-semibold text-md text-blue-700 mb-2">
                      Document Requests
                    </h3>
                    {selected.document_requests.length === 0 ? (
                      <div className="text-gray-400">No document requests.</div>
                    ) : (
                      <ul className="list-disc pl-5">
                        {selected.document_requests.map((req, idx) => (
                          <li key={idx} className="mb-2">
                            {JSON.stringify(req)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mb-4 flex gap-4">
                    <button
                      className="text-blue-600 hover:underline font-semibold mb-2 text-left"
                      onClick={() => handleShowSummary(selected.appointment_id)}
                    >
                      View Appointment Summary
                    </button>
                  </div>
                  {summary && (
                    <div className="bg-blue-50 text-blue-900 rounded p-3 mb-4 text-sm whitespace-pre-line">
                      <strong>Summary:</strong> {summary}
                    </div>
                  )}
                  <button
                    className={`mt-auto px-4 py-2 rounded-lg font-semibold transition ${
                      selected.status === "accepted"
                        ? "bg-green-500 text-white cursor-default"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                    disabled={selected.status === "accepted"}
                    onClick={() => handleAccept(selected.appointment_id)}
                  >
                    {selected.status === "accepted"
                      ? "Accepted"
                      : "Accept Appointment"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsPage;
